import {
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";

import { UserRole } from "@/utils/roleManager";

interface SidebarItemProps {
  label: string;
  path: string;
  icon: React.ReactNode;
  minimumRole: UserRole;
  onClick?: () => void;
  // Called after a selection is made (after navigation or custom onClick)
  afterSelect?: () => void;
  userHasPermission: (role: UserRole) => boolean;
  isDemo?: boolean;
  isMobile?: boolean;
}

export const SidebarItem: React.FC<SidebarItemProps> = ({
  label,
  path,
  icon,
  minimumRole,
  onClick,
  afterSelect,
  userHasPermission,
  isDemo = false,
  isMobile = false,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const isNavigationItem = !onClick && Boolean(path) && !path.startsWith("#");

  // Guard against accidental clicks while scrolling the sidebar
  const pointerRef = React.useRef<{ startY: number | null; moved: boolean }>({
    startY: null,
    moved: false,
  });

  if (!userHasPermission(minimumRole)) {
    return null;
  }

  const isActive =
    location.pathname === path || (path !== "#" && location.pathname.startsWith(path));

  const handleClick = (e: React.MouseEvent) => {
    if (pointerRef.current.moved) {
      // Ignore click triggered by a drag/scroll gesture
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onClick) {
      e.preventDefault();
      onClick();
    }

    const isPlainLeftClick = e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

    if (afterSelect && isPlainLeftClick) {
      afterSelect();
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pointerRef.current.startY = e.clientY;
    pointerRef.current.moved = false;
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const s = pointerRef.current.startY;
    if (s != null && Math.abs(e.clientY - s) > 6) {
      pointerRef.current.moved = true;
    }
  };
  const onPointerUp = () => {
    // Reset start after click processing
    setTimeout(() => {
      pointerRef.current.startY = null;
    }, 0);
  };

  return (
    <ListItem disablePadding sx={{ mb: 0.25 }}>
      <ListItemButton
        {...(isNavigationItem ? { component: RouterLink, to: path } : {})}
        selected={isActive}
        onClick={handleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          overflow: "hidden",
          borderRadius: "12px",
          minHeight: isMobile ? "40px" : "36px",
          py: 0.625,
          px: isMobile ? 1.5 : 2,
          mx: 0.5,
          color: "primary.main",
          transition: "all 0.3s ease-in-out",
          boxShadow: "inset 0px 0px 0px 1px transparent",
          "&:hover": {
            background: isRtl
              ? "/* @noflip */ linear-gradient(225deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)"
              : "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)",
            transform: "translateX(2px)",
            boxShadow:
              "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 4px 12px rgba(79, 70, 229, 0.12)",
            border: 0,
          },
          "&.Mui-selected": {
            background: isRtl
              ? "/* @noflip */ linear-gradient(256deg, #06F 0%, #00B7FF 100%)"
              : "/* @noflip */ linear-gradient(104deg, #06F 0%, #00B7FF 100%)",
            color: "#fff",
            boxShadow:
              "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 6px 16px rgba(0, 102, 255, 0.25)",
            border: 0,
          },
          "&.Mui-selected:hover": {
            background: isRtl
              ? "/* @noflip */ linear-gradient(256deg, #06F 0%, #00B7FF 100%)"
              : "/* @noflip */ linear-gradient(104deg, #06F 0%, #00B7FF 100%)",
            color: "#fff",
            boxShadow:
              "inset 0px 0px 0px 1px rgba(79, 70, 229, 0.15), 0 6px 16px rgba(0, 102, 255, 0.25)",
            border: 0,
            transform: "translateX(2px)",
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 28,
            justifyContent: "center",
            color: "inherit",
            marginInlineStart: 0,
            marginInlineEnd: theme.spacing(1.5),
          }}
        >
          {icon}
        </ListItemIcon>

        <ListItemText
          sx={{
            flex: "1 1 auto",
            m: 0,
            textAlign: "start",
            "& .MuiListItemText-primary": {
              width: "100%",
            },
          }}
          primary={
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: isActive ? 600 : 400,
                color: "inherit",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "start",
              }}
            >
              {label}
            </Typography>
          }
        />
        {isDemo && (
          <Box
            sx={{
              fontSize: "0.5rem",
              fontWeight: 500,
              color: "rgba(79, 70, 229, 0.7)",
              background:
                "/* @noflip */ linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(0, 102, 255, 0.08) 100%)",
              border: "1px solid rgba(79, 70, 229, 0.2)",
              borderRadius: "6px",
              px: 0.5,
              py: 0.1,
              lineHeight: 1,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginInlineStart: "auto",
              marginInlineEnd: 0,
              flexShrink: 0,
            }}
          >
            Demo
          </Box>
        )}
      </ListItemButton>
    </ListItem>
  );
};
